import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"
import DirtyForm from "../lib/dirty_form"
import { markSubmitted, clearSubmitMark, shouldForceCloseOnRender } from "../lib/modal_submit_mark"

export class BaseModal extends Controller {
  connect() {
    new bootstrap.Modal(this.element)

    this.element.addEventListener("show.bs.modal", this.rememberOpener.bind(this))
    this.element.addEventListener("show.bs.modal", this.showModal.bind(this))
    this.element.addEventListener("shown.bs.modal", this.focusModal.bind(this))
    this.element.addEventListener("hide.bs.modal", this.confirmDismissal.bind(this))
    this.element.addEventListener("hide.bs.modal", this.releaseModalFocus.bind(this))
    this.element.addEventListener("hidden.bs.modal", this.hideModal.bind(this))

    // A successful own-form submit is the one thing that lets the following
    // refresh morph close this modal (see _hideBeforeRender). The mark
    // lives on the element so remote_modal_controller (a second controller
    // on this same element) reads the same state.
    this.element.addEventListener("submit", this._markSubmitted.bind(this))
    this.element.addEventListener("turbo:submit-end", this._clearSubmitMarkOnFailure.bind(this))

    // Turbo renders (including morphing page refreshes) reset the modal's
    // DOM without running Bootstrap's teardown, stranding the backdrop.
    // Pause the render, hide cleanly, then resume.
    this._hideBeforeRender = this._hideBeforeRender.bind(this)
    document.addEventListener("turbo:before-render", this._hideBeforeRender)

    this.afterModalSetup?.()
  }

  disconnect() {
    document.removeEventListener("turbo:before-render", this._hideBeforeRender)
    this._cleanUpModal()
  }

  _hideBeforeRender(event) {
    if (!this.element.classList.contains("show")) { return }

    // Morph renders (turbo_stream.refresh) only close the modal whose own
    // form just submitted — the refresh is that save's cue to close. Any
    // OTHER open modal is data-turbo-permanent while shown (see
    // setup/modal_morph_persistence) and rides the morph out with its form
    // state intact. Full-page renders still close everything, as before.
    if (!shouldForceCloseOnRender(this.element, event)) { return }

    clearSubmitMark(this.element)
    event.preventDefault()
    this.element.addEventListener("hidden.bs.modal", event.detail.resume, { once: true })
    // A render-forced close is not a user dismissal, so skip the dirty
    // confirm: declining would cancel the hide and strand the paused
    // render (resume only runs on hidden.bs.modal). Successful submits
    // are clean by the time their refresh morph lands anyway.
    this.element._skipDirtyConfirm = true
    this.bsModal?.hide()
    this.element._skipDirtyConfirm = false
  }

  showModal(event) {
    // Ignore events bubbling up from a modal nested inside this one.
    if (event && event.target !== this.element) { return }

    this._setModalTitle(event)
  }

  // The container renders with data-bs-focus="false" (see
  // Modals::BaseModalComponent) so Bootstrap's FocusTrap can't fight
  // widgets that append their dropdown to <body> (slim-select, flatpickr).
  // Side effect: Bootstrap no longer moves focus into the modal on show,
  // so focus stays on the trigger link outside it — and Bootstrap's Escape
  // handler is bound to the modal element, so the keydown never reaches it
  // and Escape can't dismiss. Move focus onto the modal once after it is
  // shown (the container renders with tabindex="-1"). One-time, not a
  // trap: nothing re-yanks focus afterwards, so the pickers keep working.
  focusModal(event) {
    // Ignore events bubbling up from a modal nested inside this one.
    if (event && event.target !== this.element) { return }
    if (this.element.contains(document.activeElement)) { return }

    this.element.focus({ preventScroll: true })
  }

  // Closing a dialog has to put focus back where it came from, or a keyboard
  // user is returned to the top of the document and has to tab all the way
  // back (WCAG 2.4.3). releaseModalFocus below is where that happens; this
  // just records the target on the way in.
  //
  // Bootstrap's data-api restores focus itself, but only for modals opened by
  // a [data-bs-toggle] click. Ours also open from Stimulus actions and from
  // remote_modal_controller's connect(), which nothing refocuses.
  rememberOpener(event) {
    if (event && event.target !== this.element) { return }

    // relatedTarget is set for data-api opens and nested_modals' explicit
    // show(trigger). Remote modals show themselves on connect with neither,
    // so fall back to whatever the user was on when the modal appeared.
    const opener = event?.relatedTarget || document.activeElement

    this._opener = opener && !this.element.contains(opener) ? opener : null
  }

  // Dismissing a modal discards its forms (hideModal resets them below),
  // so if one is dirty, ask first — same message and semantics as
  // DirtyForm's turbo:before-visit prompt. hide.bs.modal is cancelable and
  // fires for every dismissal path: Escape, the ✕ button, data-bs-dismiss
  // buttons, backdrop clicks, and programmatic hide() calls. Successful
  // submits never prompt: the submit event clears the dirty flag before
  // any hide runs. Descendant scope keeps stacking safe — an inner modal's
  // controller only prompts for forms inside the inner modal.
  confirmDismissal(event) {
    // Ignore events bubbling up from a modal nested inside this one.
    if (event && event.target !== this.element) { return }
    // Flag lives on the element: remote_modal_controller render-forces
    // hides on this same element from its own controller instance.
    if (this.element._skipDirtyConfirm) { return }

    const dirtyForm = this.element.querySelector("form[data-dirty]")
    if (!dirtyForm) { return }

    const message = dirtyForm.dataset.dirtyFormMessageValue || DirtyForm.DEFAULT_MESSAGE
    if (!confirm(message)) {
      event.preventDefault()
    }
  }

  // Bootstrap sets aria-hidden="true" on the closed modal, but with
  // data-bs-focus="false" its FocusTrap never ran, so nothing moves focus
  // back out — clicking ✕ leaves focus on that button and Chrome blocks
  // the attribute ("Blocked aria-hidden ... descendant retained focus").
  // So focus has to leave the modal while it is still visible.
  //
  // Where it goes matters: dropping it on <body> returns a keyboard user to
  // the top of the document with no idea where they were (WCAG 2.4.3), so
  // prefer the element that opened the modal and fall back to a blur only
  // when there isn't a usable one.
  //
  // This runs on hide, not hidden, because hidden is not reliable here: a
  // remote modal's turbo-frame detaches the element before Bootstrap's
  // transition callback lands, and the event never fires at all.
  //
  // Stacked modals already re-focus the one underneath on hidden.bs.modal
  // (nested_modals) — skip those, blurring mid-stack makes closing the
  // inner modal prompt the parent's dirty-form confirm
  // (metric_modal_stacking_spec fails without the guard).
  releaseModalFocus(event) {
    if (event && event.target !== this.element) { return }
    if (event?.defaultPrevented) { return }
    if (document.querySelectorAll(".modal.show").length > 1) { return }
    if (!this.element.contains(document.activeElement)) { return }

    const target = this._focusTargetOnClose()
    this._opener = null

    target?.focus({ preventScroll: true })

    // focus() is a no-op on a target that can't take it — <body>, when
    // nothing focusable opened the modal — so make sure focus really left.
    if (this.element.contains(document.activeElement)) { document.activeElement.blur() }
  }

  // The opener itself where it can still take focus, otherwise the control
  // that revealed it. A morph-forced close usually replaces the trigger, and
  // destructive actions are typically menu items inside a more-actions
  // dropdown that closes as the modal opens — in both cases the opener is
  // detached or invisible, and the dropdown's toggle is where the user was
  // before they opened the menu.
  _focusTargetOnClose() {
    const focusable = element =>
      element?.isConnected && element.getClientRects().length ? element : null

    const opener = this._opener
    if (!opener) { return null }

    // Toggle and menu are siblings here rather than wrapped in a .dropdown —
    // same traversal nested_modals.js uses to close open menus.
    const menu = opener.closest(".dropdown-menu")
    const toggle = menu?.parentElement?.querySelector('[data-bs-toggle="dropdown"]')

    return focusable(opener) || focusable(toggle)
  }

  hideModal(event) {
    // Ignore events bubbling up from a modal nested inside this one —
    // resetting the outer modal's forms would wipe unsaved input.
    if (event && event.target !== this.element) { return }

    // Closed is closed — a stale submit mark must not let a later render
    // think this modal still wants force-closing.
    clearSubmitMark(this.element)
    this._resetForms()
    this._resetMultiSelects()
    this._resetFlatpickrs()
  }

  teardown() {
    this.hideModal()
  }

  // private
  // Submits bubbling up from a modal nested inside this one mark only the
  // inner modal — the outer modal did not save and must stay open.
  _markSubmitted(event) {
    if (event.target.closest(".modal") !== this.element) { return }

    markSubmitted(this.element)
  }

  // A failed submit (e.g. 422 validation) leaves the modal open and
  // editable — the mark must not linger, or a later background refresh
  // would close the modal mid-edit.
  _clearSubmitMarkOnFailure(event) {
    if (event.target.closest(".modal") !== this.element) { return }
    if (event.detail.success) { return }

    clearSubmitMark(this.element)
  }

  _resetForms() {
    // use default form resetting behavior
    this.element.querySelectorAll("form").forEach(f => f.reset())
  }

  _resetFlatpickrs() {
    // Re-sync each flatpickr with the default value form.reset() just
    // restored on its input — clear() would empty pre-filled edit forms
    // instead of resetting them, leaving DirtyForm honestly dirty. Pass
    // false so no change/input events fire mid-reset.
    this.element.querySelectorAll("input").forEach(input => {
      const picker = input?._flatpickr
      if (!picker) { return }

      if (input.defaultValue) {
        picker.setDate(input.defaultValue, false)
      } else {
        picker.clear(false)
      }
    })
  }

  _resetMultiSelects() {
    // Re-sync each slim-select with the default selection form.reset()
    // just restored on the native select — setSelected([]) would clear
    // pre-filled edit forms instead of resetting them. slim-select's
    // MutationObserver still fires a native change per select (async),
    // but DirtyForm recomputes and stays clean because the restored
    // values match the initial ones. slim-select preserves the selected
    // attribute (defaultSelected) when it rebuilds option nodes.
    this.element.querySelectorAll("select").forEach(select => {
      if (!select.slimSelect) { return }

      const defaults = [...select.options].filter(o => o.defaultSelected).map(o => o.value)
      select.slimSelect.setSelected(defaults)
    })
  }

  _cleanUpModal() {
    this.bsModal?.dispose()
  }

  _setModalTitle(event) {
    if (!event.relatedTarget) { return }

    const { modalTitle } = event.relatedTarget.dataset
    const titleElement = this.element.querySelector(".modal-header .modal-title")

    if (modalTitle && titleElement) {
      titleElement.innerText = modalTitle
    }
  }

  get bsModal() {
    return bootstrap.Modal.getInstance(this.element)
  }
}